import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Sparkles, Copy, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const TOOLS = [
  { key: "boolean-search", label: "Boolean Search", desc: "Generate LinkedIn, Google & Indeed Boolean strings.", inputs: [
    { name: "role", label: "Role", placeholder: "e.g. Senior Warehouse Supervisor" },
    { name: "must_have_skills", label: "Must-have skills (comma-separated)" },
    { name: "nice_to_have_skills", label: "Nice-to-have skills" },
    { name: "location", label: "Location", placeholder: "e.g. Orlando, FL" },
    { name: "exclusions", label: "Exclusions (comma-separated)" },
  ]},
  { key: "recruiting-email", label: "Recruiting Email", desc: "Draft outreach, follow-ups, rejections, prep emails.", inputs: [
    { name: "kind", label: "Type", type: "select", options: ["candidate_outreach","employer_outreach","followup","rejection","interview_prep"] },
    { name: "context", label: "Context (role, company, candidate details, situation)", multi: true },
    { name: "tone", label: "Tone", placeholder: "warm and professional" },
  ]},
  { key: "linkedin-message", label: "LinkedIn Message", desc: "Short, punchy LinkedIn DMs (under 300 chars).", inputs: [
    { name: "kind", label: "Type", type: "select", options: ["candidate_outreach","employer_outreach","followup"] },
    { name: "context", label: "Context", multi: true },
    { name: "tone", label: "Tone", placeholder: "casual and human" },
  ]},
  { key: "rewrite-resume", label: "Rewrite Resume", desc: "Rewrite a resume for clarity and impact.", inputs: [
    { name: "text", label: "Paste resume text", multi: true },
  ]},
  { key: "proposal", label: "Employer Proposal", desc: "Generate a professional recruiting proposal.", inputs: [
    { name: "employer_name", label: "Employer name" },
    { name: "industry", label: "Industry" },
    { name: "roles", label: "Roles (comma-separated)" },
    { name: "fee_type", label: "Fee type", type: "select", options: ["percentage","flat"] },
    { name: "fee_value", label: "Fee value", type: "number" },
    { name: "notes", label: "Notes", multi: true },
  ]},
  { key: "strategy", label: "Strategy Advisor", desc: "Ask any recruiting business question — get advice.", inputs: [
    { name: "text", label: "Your question", multi: true, placeholder: "e.g. How do I get my first 5 clients?" },
  ]},
];

const LIST_FIELDS = { must_have_skills: true, nice_to_have_skills: true, exclusions: true, roles: true };

export default function AIToolkit() {
  const [selected, setSelected] = useState(TOOLS[0]);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [output, setOutput] = useState("");

  const run = async (e) => {
    e.preventDefault();
    setBusy(true); setOutput("");
    try {
      // Coerce list fields
      const body = { ...form };
      for (const k of Object.keys(LIST_FIELDS)) {
        if (typeof body[k] === "string") body[k] = body[k].split(",").map(s => s.trim()).filter(Boolean);
      }
      if (body.fee_value) body.fee_value = parseFloat(body.fee_value);
      const { data } = await api.post(`/ai/${selected.key}`, body);
      setOutput(data.text || data.raw || JSON.stringify(data, null, 2));
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI unavailable — activate your Universal Key");
    } finally { setBusy(false); }
  };

  const copy = () => { navigator.clipboard.writeText(output); toast.success("Copied"); };

  return (
    <div>
      <div className="mb-6">
        <div className="eyebrow mb-2">Success Center</div>
        <h1 className="font-display text-3xl font-bold text-navy">AI Recruiting Toolkit</h1>
        <p className="text-sm text-slate-500 mt-1">Powered by Claude Sonnet 4.5 — suggestions only; you make the final decisions.</p>
      </div>

      <div className="grid md:grid-cols-12 gap-6">
        <div className="md:col-span-3 space-y-2">
          {TOOLS.map(t => (
            <button key={t.key} onClick={() => { setSelected(t); setForm({}); setOutput(""); }}
              className={`w-full text-left rounded-lg p-3 border transition-colors ${selected.key === t.key ? "bg-navy text-white border-navy" : "bg-white border-slate-200 hover:border-navy"}`}>
              <div className="flex items-center gap-2">
                <Sparkles className={`w-4 h-4 ${selected.key === t.key ? "text-gold" : "text-slate-400"}`} />
                <div className={`text-sm font-medium ${selected.key === t.key ? "text-white" : "text-navy"}`}>{t.label}</div>
              </div>
              <div className={`text-xs mt-1 ${selected.key === t.key ? "text-slate-200" : "text-slate-500"}`}>{t.desc}</div>
            </button>
          ))}
        </div>

        <form onSubmit={run} className="md:col-span-5 border border-slate-200 rounded-xl bg-white p-6 space-y-4">
          <div>
            <div className="eyebrow mb-1">{selected.label}</div>
            <div className="text-xs text-slate-500">{selected.desc}</div>
          </div>
          {selected.inputs.map(inp => (
            <div key={inp.name}>
              <label className="text-xs text-slate-500">{inp.label}</label>
              {inp.type === "select" ? (
                <select value={form[inp.name] || inp.options[0]} onChange={e => setForm({...form, [inp.name]: e.target.value})}
                  className="w-full border rounded px-3 py-2 mt-1 text-sm">
                  {inp.options.map(o => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
                </select>
              ) : inp.multi ? (
                <textarea rows={4} value={form[inp.name] || ""} onChange={e => setForm({...form, [inp.name]: e.target.value})}
                  placeholder={inp.placeholder}
                  className="w-full border rounded px-3 py-2 mt-1 text-sm" />
              ) : (
                <input type={inp.type || "text"} value={form[inp.name] || ""} onChange={e => setForm({...form, [inp.name]: e.target.value})}
                  placeholder={inp.placeholder}
                  className="w-full border rounded px-3 py-2 mt-1 text-sm" />
              )}
            </div>
          ))}
          <button type="submit" disabled={busy} className="btn-navy w-full justify-center disabled:opacity-60">
            <Sparkles className="w-4 h-4" /> {busy ? "Working…" : `Run ${selected.label}`}
          </button>
        </form>

        <div className="md:col-span-4 border border-slate-200 rounded-xl bg-white p-6">
          <div className="flex justify-between items-center mb-3">
            <div className="eyebrow">Output</div>
            {output && <button onClick={copy} className="text-xs text-navy hover:text-gold inline-flex items-center gap-1"><Copy className="w-3 h-3" /> Copy</button>}
          </div>
          <div className="text-sm whitespace-pre-wrap text-slate-700 leading-relaxed min-h-[300px]">
            {output || <div className="text-slate-400 text-center pt-16">Results will appear here…</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
